export interface MainMenuConfig {
	toggles: string;
	styleClass: {
		menuOpen: string;
	};
	focusLock: {
		dialog: string;
		return: string;
		toggle: string;
	};
	easing: string;
	duration: number;
}

export class MainMenu {
	private focusFirstElement: HTMLElement|null = null;
	private focusLastElement?: HTMLElement;

	constructor(
		private readonly menu: HTMLElement,
		private readonly toggleOpen: HTMLElement,
		private readonly config: MainMenuConfig
	) {
		// Allows for non-js fallbacks
		menu.classList.add('js-ready');

		// Close the menu when the user activates something outside the navbar.
		document.body.addEventListener('click', event => {
			if (!(event.target instanceof HTMLElement) || this.menu.contains(event.target)) return;
			if (this.menu.ariaModal === 'true' && this.toggleOpen == event.target.closest('a')) return;
			this.menu.dispatchEvent(new CustomEvent("plaudit__hideSubmenus"));
		});

		// Open/close menu
		for (const element of document.querySelectorAll(this.config.toggles)) {
			element.addEventListener('click', event => {
				event.preventDefault();
				this.modalToggle();
			});
		}

		// Toggles mobile menu based on if this.toggleOpen is on screen
		new IntersectionObserver(entries => {
			for (const entry of entries) {
				// kinda gross, but there's just a lot of ternary
				const isDialog = entry.intersectionRatio > 0;
				// AXE devtools complains about aria-modal not being valid if "false"
				if (isDialog) {
					menu.setAttribute('aria-modal', 'true');
				} else {
					menu.removeAttribute('aria-modal');
				}
				menu.setAttribute('data-was', menu.getAttribute('role') ?? 'initial');
				menu.setAttribute('role', isDialog ? 'dialog' : '');
				for (const element of menu.querySelectorAll('.menu-item-has-children > a[aria-expanded]')) {
					element.ariaHidden = isDialog ? 'true' : 'false';
				}
				if (isDialog) {
					menu.addEventListener("transitionend", () => {
						menu.removeAttribute('data-was');
					}, {once: true});
				}
			}
		}).observe(toggleOpen);
	}

	private modalFocusLock(open: boolean) {
		const dialog = document.querySelector(this.config.focusLock.dialog);
		if (!dialog) {
			return;
		}

		const focusLockEventHandler = (event: KeyboardEvent) => {
			if (event.key === 'Tab') {
				if (event.target === this.focusFirstElement && event.shiftKey) {
					event.preventDefault();
					this.focusLastElement?.focus();
				} else if (event.target === this.focusLastElement && !event.shiftKey) {
					event.preventDefault();
					this.focusFirstElement?.focus();
				}
			}
		};

		if (open) {
			this.focusFirstElement = document.querySelector(this.config.focusLock.toggle);
			this.focusLastElement = Array.from(dialog.querySelectorAll<HTMLElement>('a, button')).pop();

			this.menu.addEventListener('keydown', focusLockEventHandler);
			this.focusFirstElement?.focus();
		} else {
			const focusReturnElement = document.querySelector<HTMLElement>(this.config.focusLock.return);
			this.menu.removeEventListener('keydown', focusLockEventHandler);
			focusReturnElement?.focus();
		}
	}

	private modalToggle(open?: boolean): void {
		if (open === undefined) {
			open = !document.body.classList.contains(this.config.styleClass.menuOpen);
		}
		// Menu Open class
		document.body.classList.toggle(this.config.styleClass.menuOpen, open);
		// Scroll locking
		document.querySelector('html')?.classList.toggle('lockScroll', open);
		// Toggle button focus: Relies on --close, --open naming convention
		document.querySelector<HTMLElement>(`${this.config.toggles}--${open ? 'close' : 'open'}`)?.focus();

		this.modalFocusLock(open);

		// Open current
		if (open) {
			this.menu.dispatchEvent(new CustomEvent("plaudit__showSubmenus"));
		}
	}
}
